// ─────────────────────────────────────────────────────────────────────────────
// analyticsConfigDocs.ts
// Field-level reference documentation for all analytics configuration forms
// in the Admin Console — visualizations, dashboards, widgets, and filters.
//
// Used to render the field reference panels in each form tab so users
// understand what each JSON key does without needing technical knowledge.
// ─────────────────────────────────────────────────────────────────────────────

export interface FieldDoc {
    field: string;
    label: string;
    description: string;
    example: string;
    note?: string;
    values?: string;
}

// ── Visualization — Query field docs (Data tab) ───────────────────────────────
export const QUERY_FIELD_DOCS: FieldDoc[] = [
    {
        field: 'module',
        label: 'module',
        description: 'Identifies which part of the platform this query belongs to. Always "COMMON".',
        example: '"module": "COMMON"',
    },
    {
        field: 'dataSource',
        label: 'dataSource',
        description: '"ES" means Elasticsearch — the platform\'s main analytics database. Always "ES".',
        example: '"dataSource": "ES"',
    },
    {
        field: 'esInstance',
        label: 'esInstance',
        description: 'Specifies which Elasticsearch cluster to query. Always "QUADNARY" for live data.',
        example: '"esInstance": "QUADNARY"',
        note: 'Contact the technical team if you are unsure which instance to use.',
    },
    {
        field: 'indexName',
        label: 'indexName',
        description: 'The Elasticsearch index to query. Each index contains a different type of data — registrations, enrollments, competencies etc.',
        example: '"indexName": "dev-aastrika-user-registration-data"',
        note: 'Use the exact index name. A wrong index name will return no data.',
    },
    {
        field: 'dateRefField',
        label: 'dateRefField',
        description: 'The date field used when a date range filter is applied on the dashboard.',
        example: '"dateRefField": "registrationDateTimestamp"',
    },
    {
        field: 'requestQueryMap',
        label: 'requestQueryMap',
        description: 'Maps dashboard filter labels (District, Block) to actual database field names.',
        example: '"requestQueryMap": "{\\\"District\\\": \\\"district\\\", \\\"Block\\\": \\\"block\\\"}"',
    },
    {
        field: 'filterPath',
        label: 'filterPath',
        description: 'Path to the nested aggregation buckets in the ES response. Required for bar, table, pie charts. Leave empty for metric and line charts.',
        example: '"filterPath": "aggregations.filtered_data.by_group.buckets"',
    },
    {
        field: 'aggrQuery',
        label: 'aggrQuery',
        description: 'The core Elasticsearch aggregation query defining what data to fetch. Replace YOUR_AGGREGATION_NAME with your actual aggregation name — must match aggregationPaths in Additional Config.',
        example: '{"size": 0, "aggs": {"filtered_data": {"filter": {...}, "aggs": {"YOUR_AGGREGATION_NAME": {"cardinality": {"field": "userId"}}}}}}',
        note: 'The aggregation name here must match aggregationPaths in the Additional Config tab.',
    },
];

// ── Visualization — Additional Config field docs (Additional Config tab) ───────
export const CONFIG_FIELD_DOCS: FieldDoc[] = [
    {
        field: 'chartType',
        label: 'chartType',
        description: 'Must match the chart type selected in the Info tab.',
        example: '"chartType": "metric"',
        values: 'metric, bar, horizontalBar, pie, line, table, scatter, bubble',
    },
    {
        field: 'aggregationPaths',
        label: 'aggregationPaths',
        description: 'Must exactly match the aggregation name used in aggrQuery on the Data tab. Replace YOUR_AGGREGATION_NAME with your actual name.',
        example: '"aggregationPaths": ["YOUR_AGGREGATION_NAME"]',
        note: 'A mismatch with aggrQuery will result in the chart showing no data.',
    },
    {
        field: 'resultType',
        label: 'resultType',
        description: 'Tells the system how to read the value returned by the aggregation.',
        example: '"resultType": "value"',
        values: 'value — reads a single number. object — reads from a nested object.',
    },
    {
        field: 'valueType',
        label: 'valueType',
        description: 'Defines how the value is formatted when displayed.',
        example: '"valueType": "number"',
        values: 'number, percentage',
    },
    {
        field: 'isDecimal',
        label: 'isDecimal',
        description: 'Whether to show decimal points. false for whole numbers, true for fractional values.',
        example: '"isDecimal": false',
    },
    {
        field: 'action',
        label: 'action',
        description: 'Mathematical operation on the query result. Leave empty for most chart types.',
        example: '"action": "COUNT_OF_RECORDS"',
        values: 'COUNT_OF_RECORDS, COUNT_OF_RECORDS_NON_ZERO, SUM_OF_VALUES, LIST_OF_ALL_VALUES',
    },
    {
        field: 'documentType',
        label: 'documentType',
        description: 'Always "_doc" for all current Elasticsearch 7.x indices.',
        example: '"documentType": "_doc"',
        note: 'Always use "_doc".',
    },
    {
        field: 'drillChart',
        label: 'drillChart',
        description: 'Set to a visualization code to enable drill-down. "none" disables it.',
        example: '"drillChart": "none"',
    },
    {
        field: 'isCumulative',
        label: 'isCumulative',
        description: 'For line charts — whether to show cumulative running totals instead of daily values.',
        example: '"isCumulative": false',
    },
    {
        field: 'interval',
        label: 'interval',
        description: 'For line charts — the time bucket size used in the date_histogram aggrQuery.',
        example: '"interval": "day"',
        values: 'day, week, month',
    },
    {
        field: '_comment',
        label: '_comment',
        description: 'Documentation note. Not displayed to users.',
        example: '"_comment": "Total registrations by district"',
    },
    {
        field: 'enableChartSearch',
        label: 'enableChartSearch',
        description: 'Stacked bar charts only. Shows a search box above the chart that lets viewers filter to a specific category (e.g. one course out of hundreds) without a new query. Toggle it below rather than editing this key by hand.',
        example: '"enableChartSearch": true',
        values: 'true, false',
    },
];
